import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Button, InputNumber, message, Descriptions, Card, Empty } from 'antd';
import { l313Api, DocumentHeader, DocumentPage } from '../../services/l313Api';
import './L313Form.css';

// ---- Filter state ----
interface FilterState {
  docNo: string;
  pageFrom: number;
  pageTo: number;
}

const emptyFilter = (): FilterState => ({
  docNo: '',
  pageFrom: 1,
  pageTo: 999,
});

// ---- Component ----
const L313Form: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>(emptyFilter());
  const [header, setHeader] = useState<DocumentHeader | null>(null);
  const [pages, setPages] = useState<DocumentPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  const docTypeLabel = (v: number): string => {
    const map: Record<number, string> = { 1: '品質手冊', 2: '作業程序書', 3: '作業指導書' };
    return map[v] || String(v);
  };

  const onQuery = useCallback(async () => {
    if (!filter.docNo.trim()) {
      message.warning('Please enter a document number');
      return;
    }
    setLoading(true);
    setQueried(false);
    try {
      const res = await l313Api.getDocument({
        docNo: filter.docNo,
        pageFrom: filter.pageFrom,
        pageTo: filter.pageTo,
      });
      setHeader(res.header);
      setPages(res.pages || []);
      setQueried(true);
      if (!res.pages || res.pages.length === 0) {
        message.info('No pages found for this document');
      } else {
        message.success(`Found ${res.total} page(s)`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || 'Query failed');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onClear = useCallback(() => {
    setFilter(emptyFilter());
    setHeader(null);
    setPages([]);
    setQueried(false);
  }, []);

  return (
    <div className="l313-form">
      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Home</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Document</a>
              <span>/</span>
              <span className="current">Document Print</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>Document Print/View</h1>
            <span className="badge">L313</span>
          </div>
        </div>

        <div className="filter-panel">
          <div className="filter-row">
            <span className="filter-label">Doc No:</span>
            <Input
              value={filter.docNo}
              onChange={(e) => setFilter((prev) => ({ ...prev, docNo: e.target.value }))}
              onKeyDown={onKeyDown}
              placeholder="Document Number"
              style={{ width: 160 }}
              allowClear
            />
            <span className="filter-label">Page From:</span>
            <InputNumber
              min={1}
              value={filter.pageFrom}
              onChange={(v) => setFilter((prev) => ({ ...prev, pageFrom: v || 1 }))}
              style={{ width: 80 }}
            />
            <span className="filter-sep">To</span>
            <InputNumber
              min={1}
              value={filter.pageTo}
              onChange={(v) => setFilter((prev) => ({ ...prev, pageTo: v || 999 }))}
              style={{ width: 80 }}
            />
            <Button type="primary" onClick={onQuery} loading={loading}>View</Button>
            <Button onClick={onClear}>Clear</Button>
          </div>
        </div>

        {queried && header && (
          <Card size="small" className="header-card" title="Document Info">
            <Descriptions size="small" column={4} bordered>
              <Descriptions.Item label="文件編號">{header.fl030_01}</Descriptions.Item>
              <Descriptions.Item label="文件名稱">{header.fl030_11}</Descriptions.Item>
              <Descriptions.Item label="文件類型">{docTypeLabel(header.fl030_16)}</Descriptions.Item>
              <Descriptions.Item label="版次">{header.fl030_15}</Descriptions.Item>
              <Descriptions.Item label="制訂日期">{header.fl030_12 ? header.fl030_12.substring(0, 10) : '-'}</Descriptions.Item>
              <Descriptions.Item label="修訂日期">{header.fl030_13 ? header.fl030_13.substring(0, 10) : '-'}</Descriptions.Item>
              <Descriptions.Item label="代碼">{header.fl030_14}</Descriptions.Item>
              <Descriptions.Item label="狀態">{header.fl030_80}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        <div className="pages-section">
          {queried ? (
            pages.length > 0 ? (
              pages.map((page) => (
                <Card
                  key={`${page.fl130_01}-${page.fl130_02}`}
                  size="small"
                  className="page-card"
                  title={`Page ${page.fl130_02}`}
                >
                  <pre className="page-content">{page.fl130_11}</pre>
                </Card>
              ))
            ) : (
              <Empty description="No pages found" />
            )
          ) : (
            <div className="empty-state">Enter a document number and click "View"</div>
          )}
        </div>

        <div className="action-bar">
          <Button danger size="large" onClick={() => navigate('/erp')}>Exit</Button>
        </div>
      </div>
    </div>
  );
};

export default L313Form;
